import { PlayerError } from '@foundation-player/error-codes';
import { Logger } from './logger';

interface FoundationGlobal {
  foundation?: {
    logger?: Logger;
  };
}

/**
 * getLogger MUST be used to retrieve a logger for a namespace at runtime.
 * Use it in any package instead of grabbing the logger yourself from the window object.
 *
 * @param namespace - A dot-separated path which locates the code which is going to use the logger. It should start with the current package's globalName.
 * @returns The preconfigured logger ready for use.
 * @example
 * ```ts
 * this.logger = getLogger("player.core.FoundationPlayer");
 * ```
 * @public
 */
export const getLogger = (namespace: string): Logger => {
  if (!namespace) {
    const error = new Error('Cannot return a logger for an empty namespace');
    Object.assign(error, { code: PlayerError.INTERNAL_PLAYER_ERROR_50000.code });
    throw error;
  }

  const loggerFromWindow = (window as unknown as FoundationGlobal).foundation?.logger;
  if (!loggerFromWindow) {
    const error = new Error("Logger on the window object wasn't initialized yet when getLogger() was called");
    Object.assign(error, { code: PlayerError.PLAYER_DEPENDENCY_MISSING_52120.code });
    throw error;
  }

  return loggerFromWindow.getLogger(namespace);
};
